/**
 * lightbox.js — fullscreen image viewer with prev/next and zoom.
 *
 * Replaces the old behaviour where clicking a gallery tile navigated to the
 * raw image URL, dumping the visitor out of the site with no way back but the
 * browser's back button.
 *
 * Any element carrying `data-lightbox="<group>"` joins a gallery; clicking one
 * opens the viewer at that index. Groups let a page host several independent
 * galleries without them bleeding into each other.
 */

import { qs, qsa, esc, lockScroll, PLACEHOLDER_IMG } from '../lib/dom.js';
import { gettext, num } from '../lib/i18n.js';

const ZOOM_STEP = 0.5;
const ZOOM_MIN = 1;
const ZOOM_MAX = 5;

const state = {
  items: [],
  index: 0,
  zoom: 1,
  panX: 0,
  panY: 0,
  open: false,
  lastFocus: null,
};

let host = null;

function ensureHost() {
  if (!host) host = qs('#nv-lightbox');
  return host;
}

/* ------------------------------------------------------------------ *
 * Rendering
 * ------------------------------------------------------------------ */

function render() {
  const el = ensureHost();
  if (!el) return;

  const item = state.items[state.index];
  if (!item) return;

  const multiple = state.items.length > 1;

  el.innerHTML = `
    <div class="nv-lightbox__bar">
      <span class="nv-lightbox__counter">
        ${multiple ? `${num(state.index + 1)} / ${num(state.items.length)}` : ''}
      </span>
      <div class="nv-lightbox__tools">
        <button type="button" class="nv-lightbox__btn" data-lb="zoom-out"
                aria-label="${gettext('Zoom out')}">
          <i class="fa-solid fa-magnifying-glass-minus" aria-hidden="true"></i>
        </button>
        <button type="button" class="nv-lightbox__btn" data-lb="zoom-reset"
                aria-label="${gettext('Reset zoom')}">
          <i class="fa-solid fa-expand" aria-hidden="true"></i>
        </button>
        <button type="button" class="nv-lightbox__btn" data-lb="zoom-in"
            aria-label="${gettext('Zoom in')}">
          <i class="fa-solid fa-magnifying-glass-plus" aria-hidden="true"></i>
        </button>
        <button type="button" class="nv-lightbox__btn" data-lb="close"
            aria-label="${gettext('Close')}">
          <i class="fa-solid fa-xmark" aria-hidden="true"></i>
        </button>
      </div>
    </div>

    <div class="nv-lightbox__stage" data-lb="stage">
      ${multiple ? `
        <button type="button" class="nv-lightbox__nav nv-lightbox__nav--prev" data-lb="prev"
                aria-label="${gettext('Previous image')}">
          <i class="fa-solid fa-chevron-left" aria-hidden="true"></i>
        </button>` : ''}

      <figure class="nv-lightbox__figure">
        <img class="nv-lightbox__img" data-lb="img"
             src="${esc(item.src || PLACEHOLDER_IMG)}"
             alt="${esc(item.caption || '')}"
             onerror="this.onerror=null;this.src='${PLACEHOLDER_IMG}'">
      </figure>

      ${multiple ? `
        <button type="button" class="nv-lightbox__nav nv-lightbox__nav--next" data-lb="next"
                aria-label="${gettext('Next image')}">
          <i class="fa-solid fa-chevron-right" aria-hidden="true"></i>
        </button>` : ''}
    </div>

    <div class="nv-lightbox__footer">
      ${item.caption ? `<p class="nv-lightbox__caption">${esc(item.caption)}</p>` : ''}
      ${multiple ? `
        <div class="nv-lightbox__thumbs">
          ${state.items.map((it, i) => `
            <button type="button" class="nv-lightbox__thumb ${i === state.index ? 'is-active' : ''}"
                    data-lb="thumb" data-index="${i}"
                    aria-label="${gettext('Image')} ${num(i + 1)}">
              <img src="${esc(it.thumb || it.src || PLACEHOLDER_IMG)}" alt=""
                   onerror="this.onerror=null;this.src='${PLACEHOLDER_IMG}'">
            </button>`).join('')}
        </div>` : ''}
    </div>
  `;

  applyTransform();
}

function applyTransform() {
  const image = qs('[data-lb="img"]', ensureHost());
  if (!image) return;

  image.style.transform =
    `translate(${state.panX}px, ${state.panY}px) scale(${state.zoom})`;
  image.classList.toggle('is-zoomed', state.zoom > 1);

  const out = qs('[data-lb="zoom-out"]', host);
  const zin = qs('[data-lb="zoom-in"]', host);
  if (out) out.disabled = state.zoom <= ZOOM_MIN;
  if (zin) zin.disabled = state.zoom >= ZOOM_MAX;
}

/* ------------------------------------------------------------------ *
 * Controls
 * ------------------------------------------------------------------ */

function resetZoom() {
  state.zoom = 1;
  state.panX = 0;
  state.panY = 0;
}

function setZoom(value) {
  state.zoom = Math.min(Math.max(value, ZOOM_MIN), ZOOM_MAX);
  if (state.zoom === 1) { state.panX = 0; state.panY = 0; }
  applyTransform();
}

function go(delta) {
  const total = state.items.length;
  if (!total) return;
  state.index = (state.index + delta + total) % total;
  resetZoom();
  render();
}

export function open(items, startIndex = 0) {
  const el = ensureHost();
  if (!el || !items?.length) return;

  state.items = items;
  state.index = Math.min(Math.max(startIndex, 0), items.length - 1);
  state.open = true;
  state.lastFocus = document.activeElement;
  resetZoom();

  el.hidden = false;
  render();
  // Next frame so the opacity transition actually runs.
  requestAnimationFrame(() => el.classList.add('is-open'));
  lockScroll(true);

  qs('[data-lb="close"]', el)?.focus();
}

export function close() {
  const el = ensureHost();
  if (!el || !state.open) return;

  state.open = false;
  el.classList.remove('is-open');
  lockScroll(false);

  setTimeout(() => {
    el.hidden = true;
    el.innerHTML = '';
  }, 200);

  state.lastFocus?.focus?.();
}

/* ------------------------------------------------------------------ *
 * Wiring
 * ------------------------------------------------------------------ */

let bound = false;

export function initLightbox() {
  const el = ensureHost();
  if (!el || bound) return;
  bound = true;

  // Clicks inside the viewer.
  el.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-lb]');
    if (!trigger) return;

    switch (trigger.dataset.lb) {
      case 'close': close(); break;
      case 'prev': go(-1); break;
      case 'next': go(1); break;
      case 'zoom-in': setZoom(state.zoom + ZOOM_STEP); break;
      case 'zoom-out': setZoom(state.zoom - ZOOM_STEP); break;
      case 'zoom-reset': resetZoom(); applyTransform(); break;
      case 'thumb': {
        state.index = Number(trigger.dataset.index) || 0;
        resetZoom();
        render();
        break;
      }
      case 'stage':
        // Clicking the backdrop (but not the image) dismisses.
        if (event.target === trigger) close();
        break;
      default: break;
    }
  });

  // Double-click toggles between fit and 2x.
  el.addEventListener('dblclick', (event) => {
    if (!event.target.closest('[data-lb="img"]')) return;
    setZoom(state.zoom > 1 ? 1 : 2);
  });

  // Wheel zoom, but only while the viewer owns the screen.
  el.addEventListener('wheel', (event) => {
    if (!state.open || !event.target.closest('[data-lb="img"]')) return;
    event.preventDefault();
    setZoom(state.zoom + (event.deltaY < 0 ? 0.25 : -0.25));
  }, { passive: false });

  // Drag to pan once zoomed in.
  let dragging = false;
  let startX = 0;
  let startY = 0;

  el.addEventListener('pointerdown', (event) => {
    const image = event.target.closest('[data-lb="img"]');
    if (!image || state.zoom <= 1) return;
    dragging = true;
    startX = event.clientX - state.panX;
    startY = event.clientY - state.panY;
    image.classList.add('is-panning');
    image.setPointerCapture?.(event.pointerId);
  });

  el.addEventListener('pointermove', (event) => {
    if (!dragging) return;
    state.panX = event.clientX - startX;
    state.panY = event.clientY - startY;
    applyTransform();
  });

  const endDrag = () => {
    if (!dragging) return;
    dragging = false;
    qs('[data-lb="img"]', el)?.classList.remove('is-panning');
  };

  el.addEventListener('pointerup', endDrag);
  el.addEventListener('pointercancel', endDrag);

  // Keyboard. Arrows are mapped to physical direction because the viewer is
  // forced to LTR — see the note in _gallery.css.
  document.addEventListener('keydown', (event) => {
    if (!state.open) return;
    switch (event.key) {
      case 'Escape': close(); break;
      case 'ArrowLeft': go(-1); break;
      case 'ArrowRight': go(1); break;
      case '+': case '=': setZoom(state.zoom + ZOOM_STEP); break;
      case '-': setZoom(state.zoom - ZOOM_STEP); break;
      case '0': resetZoom(); applyTransform(); break;
      default: break;
    }
  });

  // Delegated opener for anything marked up as a lightbox trigger.
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-lightbox]');
    if (!trigger) return;

    event.preventDefault();

    const group = trigger.dataset.lightbox;
    const peers = qsa(`[data-lightbox="${CSS.escape(group)}"]`);
    const items = peers.map((node) => ({
      src: node.dataset.lightboxSrc || node.querySelector('img')?.src || '',
      thumb: node.querySelector('img')?.src || '',
      caption: node.dataset.lightboxCaption || '',
    }));

    open(items, peers.indexOf(trigger));
  });
}

/**
 * Markup helper for a gallery grid whose tiles open the viewer.
 * `images` is `[{ image, caption }]` as returned by the API.
 */
export function galleryMarkup(images, group = 'gallery') {
  if (!images?.length) return '';

  return `
    <div class="nv-gallery">
      ${images.map((item) => `
        <a href="${esc(item.image || '#')}" class="nv-gallery__item"
           data-lightbox="${esc(group)}"
           data-lightbox-src="${esc(item.image || '')}"
           data-lightbox-caption="${esc(item.caption || '')}">
          <img src="${esc(item.image || PLACEHOLDER_IMG)}" alt="${esc(item.caption || '')}"
               loading="lazy" decoding="async"
               onerror="this.onerror=null;this.src='${PLACEHOLDER_IMG}'">
          <span class="nv-gallery__zoom">
            <i class="fa-solid fa-magnifying-glass-plus" aria-hidden="true"></i>
          </span>
          ${item.caption ? `
            <span class="nv-gallery__overlay">
              <span class="nv-gallery__caption">${esc(item.caption)}</span>
            </span>` : ''}
        </a>`).join('')}
    </div>`;
}
